import {
  DIGITAL_LOAN_STEPS,
} from 'constants/digitalLoan';
import {
  PHASE2_MOCK_QUERY_KEY,
  PHASE2_MOCK_SCENARIO_DEFAULT,
  PHASE2_MOCK_SCENARIOS,
  REQUIRED_ANGLE_PHOTOS,
  GUARANTEE_MOCK_QUERY_KEY,
  GUARANTEE_MOCK_SUCCESS_VALUE,
  GUARANTEE_MOCK_STORAGE_KEY,
} from 'constants/digitalLoanPhase2';
import { createEmptyPhotos } from 'modules/digitalLoan/mocks';
import { emptyManualCatalogValues } from './catalogOtherOption';

/** Normaliza un escenario mock ya resuelto (sin leer la URL). */
export function normalizePhase2MockScenario(scenario) {
  const allowed = Object.values(PHASE2_MOCK_SCENARIOS);
  if (allowed.includes(scenario)) {
    return scenario;
  }
  return PHASE2_MOCK_SCENARIO_DEFAULT;
}

/**
 * Lee digitalLoanPhase2Mock del search de entrada.
 * Usar solo al montar la ruta; no en cada step.
 */
export function getInitialPhase2MockScenario(search = '') {
  const raw = String(search || '');
  const query = raw.startsWith('?') ? raw.slice(1) : raw;
  const params = new URLSearchParams(query);
  return normalizePhase2MockScenario(params.get(PHASE2_MOCK_QUERY_KEY));
}

export function isPhase2MockForced(search = '') {
  if (process.env.NODE_ENV === 'production') {
    return false;
  }
  const raw = String(search || '');
  const query = raw.startsWith('?') ? raw.slice(1) : raw;
  const params = new URLSearchParams(query);
  return params.has(PHASE2_MOCK_QUERY_KEY);
}

/**
 * TEMP DEMO BYPASS remover cuando carlos termine el back
 */
export function isGuaranteeMockSuccessEnabled(search = '') {
  const raw = String(search || '');
  const query = raw.startsWith('?') ? raw.slice(1) : raw;
  const params = new URLSearchParams(query);
  return params.get(GUARANTEE_MOCK_QUERY_KEY) === GUARANTEE_MOCK_SUCCESS_VALUE;
}

/**
 *  * TEMP DEMO BYPASS remover cuando carlos termine el back
 * Persiste el flag en sessionStorage para sobrevivir navega que dropean el search.
 */
export function snapshotGuaranteeMockSuccessFromSearch(search = '') {
  if (!isGuaranteeMockSuccessEnabled(search)) {
    return false;
  }
  try {
    window.sessionStorage.setItem(GUARANTEE_MOCK_STORAGE_KEY, '1');
  } catch (error) {
    // ignore storage errors in demo bypass
  }
  return true;
}

/**
 * TEMP DEMO BYPASS:remover cuando carlos termine el back
 * URL actual O snapshot en sessionStorage.
 */
export function resolveGuaranteeMockSuccessEnabled(search = '') {
  if (isGuaranteeMockSuccessEnabled(search)) {
    return true;
  }
  try {
    return window.sessionStorage.getItem(GUARANTEE_MOCK_STORAGE_KEY) === '1';
  } catch (error) {
    return false;
  }
}

/** TEMP DEMO BYPASSremover cuando carlos termine el back. */
export function clearGuaranteeMockSuccessSnapshot() {
  try {
    window.sessionStorage.removeItem(GUARANTEE_MOCK_STORAGE_KEY);
  } catch (error) {
    // ignore
  }
}

/** TEMP DEMO BYPASS remover cuando carlos termine el back */
export function buildGuaranteeMockSuccessResult() {
  return {
    status: 'validated',
    valid: true,
    raw: null,
    demoBypass: true,
  };
}

export const INITIAL_PHASE2_STATE = Object.freeze({
  hasGuaranteeProduct: null,
  selectedFamily: null,
  selectedSubFamily: null,
  selectedArticle: null,
  selectedBrand: null,
  selectedModel: null,
  productCondition: '',
  estimatedValue: '',
  estimatedValueLocked: false,
  manualCatalogStartLevel: null,
  manualCatalogValues: Object.freeze(emptyManualCatalogValues()),
  photos: createEmptyPhotos(),
  photoConsents: {
    hasInvoice: false,
    isOwner: false,
    cameraAuthorized: false,
  },
  guaranteeValidationResult: null,
  scoringResult: null,
  preOffer: null,
  selectedVisitDate: null,
  selectedVisitTimeSlot: null,
});

export function createInitialPhase2State() {
  return {
    ...INITIAL_PHASE2_STATE,
    photos: createEmptyPhotos(),
    photoConsents: { ...INITIAL_PHASE2_STATE.photoConsents },
    manualCatalogValues: emptyManualCatalogValues(),
  };
}

export function clearGuaranteeProductSelection(phase2) {
  return {
    ...phase2,
    selectedFamily: null,
    selectedSubFamily: null,
    selectedArticle: null,
    selectedBrand: null,
    selectedModel: null,
    productCondition: '',
    estimatedValue: '',
    estimatedValueLocked: false,
    manualCatalogStartLevel: null,
    manualCatalogValues: emptyManualCatalogValues(),
    photos: createEmptyPhotos(),
    photoConsents: { ...INITIAL_PHASE2_STATE.photoConsents },
    guaranteeValidationResult: null,
    scoringResult: null,
    preOffer: null,
    selectedVisitDate: null,
    selectedVisitTimeSlot: null,
  };
}

/**
 * Fotos de prenda requeridas: FRONTAL + 5 ángulos.
 * ETIQUETA es opcional. OTRO/documento solo si requireDocument.
 */
/**
 * Sustituye un solo slot. El índice de ángulo es estable (0 = trasera).
 * No toca el resto de las fotos.
 */
export function replacePhotoSlot(photos, slot, file) {
  const current = {
    ...createEmptyPhotos(),
    ...(photos || {}),
    angles: [...(photos?.angles || [])],
  };

  if (slot === 'front' || slot === 'label' || slot === 'document') {
    return { ...current, [slot]: file };
  }

  const match = /^angle-(\d+)$/.exec(String(slot || ''));
  if (!match) return current;

  const index = Number(match[1]);
  if (!Number.isInteger(index) || index < 0 || index >= REQUIRED_ANGLE_PHOTOS) {
    return current;
  }

  const nextAngles = [...current.angles];
  nextAngles[index] = file;
  return {
    ...current,
    angles: nextAngles.slice(0, REQUIRED_ANGLE_PHOTOS),
  };
}

/**
 * Imagen que ven las pantallas posteriores.
 * Una respuesta nueva de /guarantee gana al snapshot, aunque la URL anterior exista.
 */
export function resolveDisplayedGuaranteeImage({
  freshImage,
  hasFreshImage = false,
  preOfferImage,
  currentImage,
  snapshotImage,
} = {}) {
  if (hasFreshImage) return freshImage || null;
  if (preOfferImage) return preOfferImage;
  if (currentImage) return currentImage;
  return snapshotImage || null;
}

export function areRequiredPhotosComplete(photos, requireDocument = false) {
  const angles = photos?.angles || [];
  const filledAngles = angles.filter(Boolean).length;
  return (
    !!photos?.front
    && filledAngles >= 5
    && (!requireDocument || !!photos?.document)
  );
}

export function buildGuaranteeDisplayName(phase2) {
  const parts = [
    phase2.selectedArticle?.name,
    phase2.selectedBrand?.name,
    phase2.selectedModel?.name,
  ].filter(Boolean);
  return parts.join(' ') || 'Prenda en garantía';
}

function hasAverageValue(model) {
  const num = Number(model?.valorPromedio);
  return Number.isFinite(num) && num > 0;
}

function readModelDescription(model) {
  return String(model?.description || model?.raw?.DESCRIPCION || '').trim();
}

/** DESCRIPCION completa si homologado o tiene VALOR_PROMEDIO. */
export function resolveGuaranteeDescription(phase2) {
  const model = phase2?.selectedModel;
  const description = readModelDescription(model);
  if (!description) return '';
  if (model?.homologado === true || hasAverageValue(model)) {
    return description;
  }
  return '';
}

export function resolveGuaranteeDisplayName(phase2) {
  return resolveGuaranteeDescription(phase2) || buildGuaranteeDisplayName(phase2);
}

export function mapScoringStatusToStep(status) {
  switch (status) {
    case PHASE2_MOCK_SCENARIOS.VALIDATION_FAILED:
      return DIGITAL_LOAN_STEPS.VALIDATION_FAILED;
    case PHASE2_MOCK_SCENARIOS.INSUFFICIENT_VALUE:
      return DIGITAL_LOAN_STEPS.INSUFFICIENT_VALUE;
    case PHASE2_MOCK_SCENARIOS.MISSING_ONBOARDING:
      return DIGITAL_LOAN_STEPS.MISSING_ONBOARDING;
    case PHASE2_MOCK_SCENARIOS.NOT_PREAPPROVED:
      return DIGITAL_LOAN_STEPS.NOT_PREAPPROVED;
    case PHASE2_MOCK_SCENARIOS.PREOFFER:
    case PHASE2_MOCK_SCENARIOS.SUCCESS:
    default:
      return DIGITAL_LOAN_STEPS.PRE_OFFER;
  }
}
